import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

interface HistoryEntry {
  account_id: string;
  balance: number;
  recorded_at: string;
}

interface ChartPoint {
  date: string;
  value: number;
}

export const NET_WORTH_PERIODS = ['1m', '3m', '6m', '1y', 'all'] as const;
export type NetWorthPeriod = (typeof NET_WORTH_PERIODS)[number];

export const NET_WORTH_PERIOD_DAYS: Record<Exclude<NetWorthPeriod, 'all'>, number> = {
  '1m': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365,
};

/**
 * Fetches daily balance data for charts.
 *
 * Primary path: server-side RPC (fast, pre-aggregated).
 * Fallback path: client-side computation from raw balance_history (if RPC fails).
 * Last resort: flat line using current account balance.
 */
export function useBalanceChart(
  accounts: Account[],
  period: number | 'all' = 30,
  accountId?: string,
  convertBalance?: (balance: number, accountId: string) => number
) {
  const { user } = useAuth();
  const [data, setData] = useState<ChartPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(0);
  const convertBalanceRef = useRef(convertBalance);
  convertBalanceRef.current = convertBalance;

  useEffect(() => {
    if (!user || accounts.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    const requestId = ++abortRef.current;
    const isStale = () => requestId !== abortRef.current;

    // Determine which accounts to include
    let visibleAccounts = accounts.filter((a) => !a.is_hidden);
    if (accountId) {
      visibleAccounts = visibleAccounts.filter((a) => a.id === accountId);
    }
    if (visibleAccounts.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    const accountIds = visibleAccounts.map((a) => a.id);
    const accountTypeMap = new Map(visibleAccounts.map((a) => [a.id, a.type]));

    // Date range
    const today = new Date();
    const toStr = today.toISOString().split('T')[0];
    const fromStr =
      period === 'all'
        ? visibleAccounts.reduce((earliest, account) => {
            const createdDate = new Date(account.created_at).toISOString().split('T')[0];
            return createdDate < earliest ? createdDate : earliest;
          }, toStr)
        : (() => {
            const from = new Date(today);
            from.setDate(from.getDate() - period);
            return from.toISOString().split('T')[0];
          })();

    const fetchData = async () => {
      setLoading(true);

      let chartPoints: ChartPoint[] | null = null;

      // ── PRIMARY: try the server-side RPC ──────────────────────
      try {
        chartPoints = await fetchViaRpc(
          accountIds,
          accountTypeMap,
          fromStr,
          toStr,
          convertBalanceRef.current
        );
      } catch {
        // RPC failed — will fall through to fallback
      }

      if (isStale()) return;

      // ── FALLBACK: client-side from raw balance_history ────────
      if (!chartPoints) {
        try {
          chartPoints = await fetchViaClientSide(
            accountIds,
            visibleAccounts,
            accountTypeMap,
            fromStr,
            toStr,
            convertBalanceRef.current
          );
        } catch {
          // Client-side also failed — will use last resort
        }
      }

      if (isStale()) return;

      // ── LAST RESORT: flat line at current net worth ───────────
      if (!chartPoints) {
        chartPoints = buildFlatLine(visibleAccounts, fromStr, toStr, convertBalanceRef.current);
      }

      setData(downsample(chartPoints));
      setLoading(false);
    };

    fetchData();
  }, [user, accounts, period, accountId]);

  return { data, loading };
}

// ═══════════════════════════════════════════════════════════════
// PRIMARY: Server-side RPC
// ═══════════════════════════════════════════════════════════════
async function fetchViaRpc(
  accountIds: string[],
  accountTypeMap: Map<string, string>,
  fromStr: string,
  toStr: string,
  convertBalance?: (balance: number, accountId: string) => number
): Promise<ChartPoint[] | null> {
  const { data: rpcData, error } = await supabase.rpc('get_daily_balance_summary', {
    p_account_ids: accountIds,
    p_from: fromStr,
    p_to: toStr,
  });

  if (error) throw error;
  if (!rpcData || rpcData.length === 0) return null;

  const dayMap = new Map<string, number>();
  for (const row of rpcData as { out_account_id: string; out_day: string; out_balance: number }[]) {
    const dayStr =
      typeof row.out_day === 'string'
        ? row.out_day
        : new Date(row.out_day).toISOString().split('T')[0];
    const type = accountTypeMap.get(row.out_account_id);
    const rawBalance = Number(row.out_balance);
    const converted = convertBalance ? convertBalance(rawBalance, row.out_account_id) : rawBalance;
    const contrib = type === 'liability' ? -Math.abs(converted) : converted;
    dayMap.set(dayStr, (dayMap.get(dayStr) || 0) + contrib);
  }

  return [...dayMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, value]) => ({ date, value: Math.round(value * 100) / 100 }));
}

// ═══════════════════════════════════════════════════════════════
// FALLBACK: Client-side computation from raw balance_history
// ═══════════════════════════════════════════════════════════════
async function fetchViaClientSide(
  accountIds: string[],
  visibleAccounts: Account[],
  accountTypeMap: Map<string, string>,
  fromStr: string,
  toStr: string,
  convertBalance?: (balance: number, accountId: string) => number
): Promise<ChartPoint[] | null> {
  // Fetch only the date range we need + carry-forward (last entry before period)
  const { data: historyData, error } = await supabase
    .from('account_balance_history')
    .select('account_id, balance, recorded_at')
    .in('account_id', accountIds)
    .order('recorded_at', { ascending: true });

  if (error) throw error;

  const history: HistoryEntry[] = historyData || [];
  const accountsWithHistory = new Set(history.map((h) => h.account_id));

  // Latest balance per account before period start (carry-forward)
  const latestBalanceByAccount = new Map<string, number>();

  // Initialize all accounts
  for (const account of visibleAccounts) {
    if (accountsWithHistory.has(account.id)) {
      latestBalanceByAccount.set(account.id, 0);
    } else {
      // Accounts without history: use current balance from created_at onward
      latestBalanceByAccount.set(account.id, 0);
    }
  }

  // Group history entries by date
  const historyByDate = new Map<string, HistoryEntry[]>();
  for (const entry of history) {
    const dateKey = new Date(entry.recorded_at).toISOString().split('T')[0];
    if (!historyByDate.has(dateKey)) {
      historyByDate.set(dateKey, []);
    }
    historyByDate.get(dateKey)!.push(entry);
  }

  // Set carry-forward balances from entries before period start
  const sortedDates = [...historyByDate.keys()].sort();
  for (const date of sortedDates) {
    if (date >= fromStr) break;
    for (const entry of historyByDate.get(date)!) {
      latestBalanceByAccount.set(entry.account_id, entry.balance);
    }
  }

  // For accounts without history that were created before period start, use current balance
  for (const account of visibleAccounts) {
    if (!accountsWithHistory.has(account.id)) {
      const createdDate = new Date(account.created_at).toISOString().split('T')[0];
      if (createdDate <= fromStr) {
        latestBalanceByAccount.set(account.id, account.balance);
      }
    }
  }

  // Generate daily data points
  const dailyData: ChartPoint[] = [];
  let currentDate = fromStr;

  while (currentDate <= toStr) {
    // Accounts without history: apply balance on their creation date
    for (const account of visibleAccounts) {
      if (!accountsWithHistory.has(account.id)) {
        const createdDate = new Date(account.created_at).toISOString().split('T')[0];
        if (currentDate === createdDate) {
          latestBalanceByAccount.set(account.id, account.balance);
        }
      }
    }

    // Apply balance changes from history
    const dayEntries = historyByDate.get(currentDate);
    if (dayEntries) {
      for (const entry of dayEntries) {
        latestBalanceByAccount.set(entry.account_id, entry.balance);
      }
    }

    // Sum net worth across all accounts
    let netWorth = 0;
    for (const [accId, balance] of latestBalanceByAccount) {
      const type = accountTypeMap.get(accId);
      const converted = convertBalance ? convertBalance(balance, accId) : balance;
      netWorth += type === 'liability' ? -Math.abs(converted) : converted;
    }

    dailyData.push({ date: currentDate, value: Math.round(netWorth * 100) / 100 });
    currentDate = addDay(currentDate);
  }

  return dailyData.length > 0 ? dailyData : null;
}

// ═══════════════════════════════════════════════════════════════
// LAST RESORT: Flat line at current net worth
// ═══════════════════════════════════════════════════════════════
function buildFlatLine(
  visibleAccounts: Account[],
  fromStr: string,
  toStr: string,
  convertBalance?: (balance: number, accountId: string) => number
): ChartPoint[] {
  const netWorth = visibleAccounts.reduce((sum, a) => {
    const converted = convertBalance ? convertBalance(a.balance, a.id) : a.balance;
    return sum + (a.type === 'liability' ? -Math.abs(converted) : converted);
  }, 0);

  const points: ChartPoint[] = [];
  let currentDate = fromStr;
  while (currentDate <= toStr) {
    points.push({ date: currentDate, value: Math.round(netWorth * 100) / 100 });
    currentDate = addDay(currentDate);
  }
  return points;
}

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════
function addDay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().split('T')[0];
}

function downsample(points: ChartPoint[]): ChartPoint[] {
  if (points.length <= 120) return points;

  const step = Math.ceil(points.length / 90);
  const sampled: ChartPoint[] = [];
  for (let i = 0; i < points.length; i += step) {
    sampled.push(points[i]);
  }
  // Always include the last point
  if (sampled.length > 0 && sampled[sampled.length - 1].date !== points[points.length - 1].date) {
    sampled.push(points[points.length - 1]);
  }
  return sampled;
}
